import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSetAtom } from 'jotai';
import { transparentize } from 'polished';
import { Button, Space, Tooltip } from 'antd';
import { filterColors } from '@utils';
import { cloneDeep, get } from 'lodash';
import { EditOutlined, PicLeftOutlined, WarningOutlined } from '@ant-design/icons';
import {
  addCompareFilterOnTrends,
  clearCompareFiltersOnTrends,
  editCompareFiltersOnTrends,
  highlightCompareFilter,
  removeCompareFiltersOnTrends,
} from '@store/trends/base/actions';
import { getFilterKey, getFilterName } from '@src/utils/filter/utils';
import { compareOnTrendsFiltersSelector } from '@store/trends/base/selectors';
import { useCompareData } from '@components/metrics-panel-hz/hooks/useCompareSeries';
import { DATA_TAGS } from '@components/metrics-panel-hz/hooks/useData';
import { primaryFilterVisibilityAtom } from '@src/components/metrics-panel-hz/atoms';
import {
  COMPARE_FILTERS_BAR_ID,
  COMPARE_SERIES_LOADING_STATE_EVENT,
  scrollToTables,
} from './utils';
import SummaryTable from './SummaryTable';
import {
  ComparePanelWrapper,
  StyledClearAll,
  StyledEllipsisTooltip,
  StyledTag,
  VerticalBar,
  VerticalLoadingBar,
  Wrapper,
} from './styled';
import { realTimeModeOnTrendsSelector } from '@src/store/common/selectors';
import { useFilterContainsInvalidField } from '@src/hooks/useSetError';
import { useFilterErrorMsg } from '../FilterDropdown/FilterStatus';
import { checkFeature } from '@src/utils/platform/features/checkFeature';
import { useInvalidDimensionCheck } from '@src/utils/filter/useInvalidDimensionCheck';
import SavedFilters from '@src/components/common/Compare/SavedFilters';
import shortId from '@src/utils/shortId';

const CONNECTION_ERROR = 'Connection failure. Please remove and add the filter again to compare.';

function CompareFiltersBar() {
  const compareFilters = useSelector(compareOnTrendsFiltersSelector);
  return compareFilters.length > 0 ? <Content compareFilters={compareFilters} /> : null;
}

function Content({ compareFilters }) {
  const dispatch = useDispatch();
  const [showSummary, setShowSummary] = useState(false);
  const { loadingState } = useCompareData({ dataTag: DATA_TAGS.compare });
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const setShowPrimarySeries = useSetAtom(primaryFilterVisibilityAtom);
  const invalidCustomTagCheck = useInvalidDimensionCheck();

  const onClear = () => {
    dispatch(clearCompareFiltersOnTrends());
  };

  const onEdit = () => {
    dispatch(editCompareFiltersOnTrends());
    scrollToTables();
  };

  const toggleCompareSummaryTable = () => {
    setShowSummary((showSummary) => !showSummary);
  };

  useEffect(() => {
    // enable primary series on unmount
    return () => {
      setShowPrimarySeries(true);
    };
  }, []);

  useEffect(() => {
    // clear compare filters on RT mode
    if (realTimeOnTrends) {
      onClear();
    }
  }, [realTimeOnTrends]);

  const onAddSavedFilter = (savedFilter) => {
    const filter = cloneDeep(savedFilter);
    filter.meta = { cid: shortId(10) };
    dispatch(addCompareFilterOnTrends(filter));
  };

  return (
    <ComparePanelWrapper>
      <Wrapper id={COMPARE_FILTERS_BAR_ID}>
        <Button icon={<PicLeftOutlined />} onClick={toggleCompareSummaryTable} />
        <div style={{ width: 80, lineHeight: 1.25 }}> {`Compare Filters`} </div>
        <div style={{ flex: 1, gap: 2, display: 'flex', flexWrap: 'wrap' }}>
          {compareFilters.map((filter, idx) => (
            <FilterTag
              filter={filter}
              idx={idx}
              key={getFilterKey(filter)}
              loading={get(loadingState, idx, null)}
              invalidCustomTagCheck={invalidCustomTagCheck}
            />
          ))}
          {checkFeature('trends.base.saved-filter-in-compare-filter', true) && (
            <SavedFilters compareFilters={compareFilters} onSelect={onAddSavedFilter} />
          )}
        </div>
        <Space>
          <Button icon={<EditOutlined />} onClick={onEdit} />
          <StyledClearAll onClick={onClear}>Clear All</StyledClearAll>
        </Space>
      </Wrapper>
      {showSummary && <SummaryTable />}
    </ComparePanelWrapper>
  );
}

function FilterTag({ filter, idx, loading, invalidCustomTagCheck }) {
  const dispatch = useDispatch();
  const filterName = getFilterName(filter);

  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState(false);

  const highlight = get(filter, ['meta', 'highlight'], false);

  // react query is active only when loading is in progress
  // which indicates request is in progress or in queue
  const filterColor = loading && !enabled ? '#aaafb4' : filterColors[idx];

  const invalidDimensionWarning = invalidCustomTagCheck(filter.rule);

  const handleLoadingStateChange = useCallback(
    ({ detail }: any) => {
      const { enabled = false, error = false } = detail;
      if ('filter' in detail) {
        // update specific filter tag
        if (filter.rule === detail.filter.rule) {
          setError(error);
          setEnabled(enabled);
        }
        // else ignore event
      } else {
        // bulk update all tags
        setError(error);
        setEnabled(enabled);
      }
    },
    [filter],
  );

  // compare state event listener
  useEffect(() => {
    document.addEventListener(COMPARE_SERIES_LOADING_STATE_EVENT, handleLoadingStateChange);
    return () =>
      document.removeEventListener(COMPARE_SERIES_LOADING_STATE_EVENT, handleLoadingStateChange);
  }, [handleLoadingStateChange]);

  const onClose = () => {
    dispatch(removeCompareFiltersOnTrends(idx));
  };

  const handleClick = () => {
    dispatch(highlightCompareFilter(idx, !highlight));
  };
  const filterError = useFilterContainsInvalidField(filter);
  const filterErrorMsg = useFilterErrorMsg(filter);

  return (
    <StyledTag
      onClick={handleClick}
      closable
      onClose={onClose}
      $highlight={highlight ? transparentize(0.75, filterColor) : '#f2f2f2'}
    >
      {/* on error show a warning icon else show an animated bar or static bar with filter color */}
      {error || filterError ? (
        <Tooltip title={filterError ? filterErrorMsg : CONNECTION_ERROR}>
          <WarningOutlined style={{ color: '#f5222d' }} />
        </Tooltip>
      ) : loading && enabled ? (
        <VerticalLoadingBar color={filterColor} loading={loading} />
      ) : (
        <VerticalBar color={filterColor} />
      )}
      <StyledEllipsisTooltip tooltipText={filterName}>{filterName}</StyledEllipsisTooltip>
      {invalidDimensionWarning}
    </StyledTag>
  );
}

export default CompareFiltersBar;
